import React, { useRef, useEffect, useState, ReactNode } from 'react';
import { Box, useTheme, useMediaQuery } from '@mui/material';

// Типы жестов
export type GestureType = 'swipe' | 'pinch' | 'longpress' | 'doubletap' | 'pan';

export interface GestureEvent {
  type: GestureType;
  direction?: 'left' | 'right' | 'up' | 'down';
  distance?: number;
  scale?: number;
  center?: { x: number; y: number };
  duration?: number;
  originalEvent: TouchEvent | PointerEvent;
}

export interface TouchGestureWrapperProps {
  children: ReactNode;
  onGesture?: (gesture: GestureEvent) => void;
  onSwipeLeft?: (event: GestureEvent) => void;
  onSwipeRight?: (event: GestureEvent) => void;
  onSwipeUp?: (event: GestureEvent) => void;
  onSwipeDown?: (event: GestureEvent) => void;
  onPinch?: (event: GestureEvent) => void;
  onLongPress?: (event: GestureEvent) => void;
  onDoubleTap?: (event: GestureEvent) => void;
  onPan?: (event: GestureEvent) => void;
  
  // Настройки чувствительности
  swipeThreshold?: number;
  longPressDelay?: number;
  doubleTapDelay?: number;
  pinchThreshold?: number;
  
  // Параметры поведения
  preventDefault?: boolean;
  stopPropagation?: boolean;
  enableSwipe?: boolean;
  enablePinch?: boolean;
  enableLongPress?: boolean;
  enableDoubleTap?: boolean;
  enablePan?: boolean;
  
  // Стили
  className?: string;
  style?: React.CSSProperties;
}

interface TouchPoint {
  x: number;
  y: number;
  timestamp: number;
}

const TouchGestureWrapper: React.FC<TouchGestureWrapperProps> = ({
  children,
  onGesture,
  onSwipeLeft,
  onSwipeRight,
  onSwipeUp,
  onSwipeDown,
  onPinch,
  onLongPress,
  onDoubleTap,
  onPan,
  
  swipeThreshold = 50,
  longPressDelay = 500,
  doubleTapDelay = 300,
  pinchThreshold = 0.1,
  
  preventDefault = false,
  stopPropagation = false,
  enableSwipe = true,
  enablePinch = true,
  enableLongPress = true,
  enableDoubleTap = true,
  enablePan = true,
  
  className,
  style
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const ref = useRef<HTMLDivElement>(null);
  
  // Состояние для отслеживания касаний
  const [touchStart, setTouchStart] = useState<TouchPoint | null>(null);
  const [touchEnd, setTouchEnd] = useState<TouchPoint | null>(null);
  const [lastTap, setLastTap] = useState<TouchPoint | null>(null);
  const [longPressTimer, setLongPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [isPanning, setIsPanning] = useState(false);
  const [initialDistance, setInitialDistance] = useState<number | null>(null);
  const [currentScale, setCurrentScale] = useState(1);

  // Утилитарные функции
  const getTouchPoint = (touch: Touch): TouchPoint => ({
    x: touch.clientX,
    y: touch.clientY,
    timestamp: Date.now()
  });

  const getDistance = (touch1: Touch, touch2: Touch): number => {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  };

  const getCenter = (touch1: Touch, touch2: Touch) => ({
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2
  });

  const clearLongPressTimer = () => {
    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
  };

  // Обработчики событий
  const handleTouchStart = (e: TouchEvent) => {
    if (preventDefault) e.preventDefault();
    if (stopPropagation) e.stopPropagation();

    const touches = e.touches;
    
    if (touches.length === 1) {
      const t0 = touches[0];
      if (!t0) return;
      const touchPoint = getTouchPoint(t0);
      setTouchStart(touchPoint);
      setIsPanning(false);
      
      // Обработка двойного тапа
      if (enableDoubleTap && lastTap && touchPoint.timestamp - lastTap.timestamp < doubleTapDelay) {
        const distance = Math.sqrt(
          Math.pow(touchPoint.x - lastTap.x, 2) + Math.pow(touchPoint.y - lastTap.y, 2)
        );
        
        if (distance < 50) { // 50px threshold for double tap
          const gestureEvent: GestureEvent = {
            type: 'doubletap',
            center: { x: touchPoint.x, y: touchPoint.y },
            originalEvent: e
          };
          
          onDoubleTap?.(gestureEvent);
          onGesture?.(gestureEvent);
          setLastTap(null);
          return;
        }
      }
      
      setLastTap(touchPoint);
      
      // Запуск таймера для long press
      if (enableLongPress) {
        const timer = setTimeout(() => {
          const gestureEvent: GestureEvent = {
            type: 'longpress',
            center: { x: touchPoint.x, y: touchPoint.y },
            duration: longPressDelay,
            originalEvent: e
          };
          
          onLongPress?.(gestureEvent);
          onGesture?.(gestureEvent);
        }, longPressDelay);
        
        setLongPressTimer(timer);
      }
    } else if (touches.length === 2 && enablePinch) {
      clearLongPressTimer();
      const t0 = touches[0];
      const t1 = touches[1];
      if (t0 != null && t1 != null) {
        const distance = getDistance(t0, t1);
        setInitialDistance(distance);
        setCurrentScale(1);
      }
    }
  };

  const handleTouchMove = (e: TouchEvent) => {
    if (preventDefault) e.preventDefault();
    if (stopPropagation) e.stopPropagation();

    const touches = e.touches;
    
    if (touches.length === 1 && touchStart) {
      clearLongPressTimer();
      const t0 = touches[0];
      if (!t0) return;
      const currentTouch = getTouchPoint(t0);
      setTouchEnd(currentTouch);
      
      // Обработка панорамирования
      if (enablePan && !isPanning) {
        const deltaX = Math.abs(currentTouch.x - touchStart.x);
        const deltaY = Math.abs(currentTouch.y - touchStart.y);
        
        if (deltaX > 10 || deltaY > 10) {
          setIsPanning(true);
        }
      }
      
      if (isPanning && enablePan) {
        const gestureEvent: GestureEvent = {
          type: 'pan',
          distance: Math.sqrt(
            Math.pow(currentTouch.x - touchStart.x, 2) + 
            Math.pow(currentTouch.y - touchStart.y, 2)
          ),
          center: { x: currentTouch.x, y: currentTouch.y },
          originalEvent: e
        };
        
        onPan?.(gestureEvent);
        onGesture?.(gestureEvent);
      }
    } else if (touches.length === 2 && enablePinch && initialDistance) {
      const t0 = touches[0];
      const t1 = touches[1];
      if (!t0 || !t1) return;
      const currentDistance = getDistance(t0, t1);
      const scale = currentDistance / initialDistance;
      
      if (Math.abs(scale - currentScale) > pinchThreshold) {
        setCurrentScale(scale);
        
        const gestureEvent: GestureEvent = {
          type: 'pinch',
          scale: scale,
          center: getCenter(t0, t1),
          originalEvent: e
        };
        
        onPinch?.(gestureEvent);
        onGesture?.(gestureEvent);
      }
    }
  };

  const handleTouchEnd = (e: TouchEvent) => {
    if (preventDefault) e.preventDefault();
    if (stopPropagation) e.stopPropagation();

    clearLongPressTimer();
    
    if (touchStart && touchEnd && enableSwipe && !isPanning) {
      const deltaX = touchEnd.x - touchStart.x;
      const deltaY = touchEnd.y - touchStart.y;
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      if (distance >= swipeThreshold) {
        let direction: 'left' | 'right' | 'up' | 'down';
        
        if (Math.abs(deltaX) > Math.abs(deltaY)) {
          direction = deltaX > 0 ? 'right' : 'left';
        } else {
          direction = deltaY > 0 ? 'down' : 'up';
        }
        
        const gestureEvent: GestureEvent = {
          type: 'swipe',
          direction,
          distance,
          center: { x: touchEnd.x, y: touchEnd.y },
          originalEvent: e
        };
        
        // Вызов специфичных обработчиков
        switch (direction) {
          case 'left':
            onSwipeLeft?.(gestureEvent);
            break;
          case 'right':
            onSwipeRight?.(gestureEvent);
            break;
          case 'up':
            onSwipeUp?.(gestureEvent);
            break;
          case 'down':
            onSwipeDown?.(gestureEvent);
            break;
        }
        
        onGesture?.(gestureEvent);
      }
    }
    
    // Сброс состояния
    setTouchStart(null);
    setTouchEnd(null);
    setIsPanning(false);
    setInitialDistance(null);
    setCurrentScale(1);
  };

  // Добавление обработчиков событий
  useEffect(() => {
    const element = ref.current;
    if (!element || !isMobile) return;

    // Преобразование в правильный тип
    const touchStartHandler = handleTouchStart as EventListener;
    const touchMoveHandler = handleTouchMove as EventListener;
    const touchEndHandler = handleTouchEnd as EventListener;

    element.addEventListener('touchstart', touchStartHandler, { passive: !preventDefault });
    element.addEventListener('touchmove', touchMoveHandler, { passive: !preventDefault });
    element.addEventListener('touchend', touchEndHandler, { passive: !preventDefault });
    element.addEventListener('touchcancel', touchEndHandler, { passive: !preventDefault });

    return () => {
      element.removeEventListener('touchstart', touchStartHandler);
      element.removeEventListener('touchmove', touchMoveHandler);
      element.removeEventListener('touchend', touchEndHandler);
      element.removeEventListener('touchcancel', touchEndHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, touchStart, touchEnd, isPanning, initialDistance, currentScale]);

  // Очистка таймера при размонтировании
  useEffect(() => {
    return () => {
      clearLongPressTimer();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      {...(ref != null ? { ref } : {})}
      {...(className != null && className !== '' ? { className } : {})}
      {...(style != null ? { style } : {})}
      sx={{
        touchAction: 'manipulation', // Оптимизация для touch
        userSelect: 'none', // Предотвращение выделения текста
        WebkitTouchCallout: 'none', // Отключение контекстного меню на iOS
        WebkitUserSelect: 'none',
        KhtmlUserSelect: 'none',
        MozUserSelect: 'none',
        msUserSelect: 'none',
      }}
    >
      {children}
    </Box>
  );
};

export default TouchGestureWrapper; 